/**
 * Utilitário para tratamento consistente de erros de licença
 */

/**
 * Trata erros de licença (503) de forma consistente
 * @param {Object} error - Objeto de erro do Axios
 * @param {string} context - Contexto da operação (ex: "buscar eventos", "buscar motoristas")
 * @returns {Error} - Erro tratado com mensagem apropriada
 */
export const handleLicenseError = (error, context = "realizar operação") => {
  if (error.response && error.response.status === 503) {
    try {
      const errorData = error.response.data;
      if (errorData && errorData.message && errorData.message.includes("licenca")) {
        return new Error(`Erro de Licença: Não há licenças disponíveis no servidor no momento. Não foi possível ${context}. Tente novamente mais tarde.`);
      }
    } catch (parseError) {
      // Se não conseguir parsear a resposta, usar mensagem padrão
    }
    return new Error(`Serviço temporariamente indisponível. Não foi possível ${context}. Tente novamente mais tarde.`);
  }
  
  // Se não for erro de licença, retorna o erro original
  return error;
};

/**
 * Verifica se um erro é relacionado a licença
 * @param {Object} error - Objeto de erro
 * @returns {boolean} - True se for erro de licença
 */
export const isLicenseError = (error) => {
  return error.response && 
         error.response.status === 503 && 
         error.response.data && 
         error.response.data.message && 
         error.response.data.message.includes("licenca");
};

/**
 * Obtém mensagem amigável para o usuário baseada no tipo de erro
 * @param {Object} error - Objeto de erro
 * @param {string} context - Contexto da operação
 * @returns {string} - Mensagem amigável para o usuário
 */
export const getErrorMessage = (error, context = "realizar operação") => {
  // Erro de licença
  if (isLicenseError(error)) {
    return `Erro de Licença: Não há licenças disponíveis no servidor no momento. Não foi possível ${context}. Tente novamente mais tarde.`;
  }
  
  // Erro SSL
  if (error.type === 'SSL_ERROR') {
    return `Erro de Certificado SSL: ${error.message}`;
  }
  
  // Erro de timeout
  if (error.code === "ECONNABORTED") {
    return `Tempo limite excedido: A operação demorou muito para responder. Tente novamente.`;
  }
  
  // Erro de rede
  if (error.request && !error.response) {
    return `Erro de Conexão: Verifique sua conexão com a internet e tente novamente.`;
  }
  
  // Erro HTTP específico
  if (error.response) {
    const status = error.response.status;
    const statusText = error.response.statusText;
    
    switch (status) {
      case 401:
        return `Erro de Autenticação: Suas credenciais não foram aceitas.`;
      case 403:
        return `Acesso Negado: Você não tem permissão para ${context}.`;
      case 404:
        return `Recurso não encontrado: O serviço solicitado não está disponível.`;
      case 500:
        return `Erro interno do servidor: Tente novamente mais tarde.`;
      case 503:
        return `Serviço temporariamente indisponível. Tente novamente mais tarde.`;
      default:
        return `Erro ${status}: ${statusText}`;
    }
  }
  
  // Erro genérico
  return `Erro inesperado: ${error.message || 'Ocorreu um problema inesperado'}`;
};
